/** @file One Shortcuts row: binding id, context, current chord (or the capture button) and its actions. */
import type { Translate } from '@pose2d/i18n/types';
import { chordLabel } from '@pose2d/shortcuts/chordLabel';
import type { KeyEventLike, Platform } from '@pose2d/shortcuts/types';
import { ChordCapture } from '../../ui-kit/ChordCapture';
import { shortcutLabelKey } from './shortcutLabelKey';
import type { ShortcutRowData } from './shortcutRows';

/** Props of ShortcutRow; ShortcutsSection binds every callback to this row. */
export interface ShortcutRowProps {
  t: Translate;
  row: ShortcutRowData;
  platform: Platform;
  capturing: boolean;
  notice: string | null;
  onCapture: () => void;
  onKey: (event: KeyEventLike) => void;
  onCancel: () => void;
  onUnbind: () => void;
  onReset: () => void;
}

/** The label is the readable name; ids and contexts are identifiers, shown as data (R-E7-06). */
export function ShortcutRow({ t, row, platform, capturing, notice, onCapture, onKey, onCancel, onUnbind, onReset }: ShortcutRowProps) {
  const labelKey = shortcutLabelKey(row.id);
  const shown = row.keys === '' ? t('shortcutsPanel.unbound') : chordLabel(row.keys, platform);
  return (
    <tr data-testid={`shortcut-${row.context}-${row.id}`}>
      <td>{labelKey === null ? null : <span className="shortcut-label">{t(labelKey)}</span>}<code className={labelKey === null ? undefined : 'shell-hint shortcut-id'}>{row.id}</code></td>
      <td className="shell-hint">{row.context}</td>
      <td>{capturing ? <ChordCapture prompt={t('shortcutsPanel.press')} label={t('shortcutsPanel.capture', { id: row.id })} onKey={onKey} onCancel={onCancel} /> : <kbd>{shown}</kbd>}</td>
      <td className="shortcut-actions">
        <button type="button" disabled={capturing} onClick={onCapture}>{t('shortcutsPanel.change')}</button>
        <button type="button" disabled={row.keys === ''} onClick={onUnbind}>{t('shortcutsPanel.unbind')}</button>
        <button type="button" disabled={!row.overridden} onClick={onReset}>{t('shortcutsPanel.reset')}</button>
        {notice === null ? null : <p role="alert" className="shell-error">{notice}</p>}
      </td>
    </tr>
  );
}
